"use client";

import { useId, useState, type ReactNode } from "react";
import { AreaChevron } from "./area-a-parts";

/**
 * The snow watch's forecast point picker (client island). Every point's header and chart are
 * server-rendered; the select only switches which one shows, by a scoped style rule, so the server
 * HTML (and the first client render) shows the highest point.
 */

export type AreaSnowWatchOption = Readonly<{ slug: string; label: string; header: ReactNode; body: ReactNode }>;

function token(value: string): string {
  return value.replace(/[^a-z0-9-]/giu, "");
}

export function AreaSnowWatchPicker({ scope, options }: Readonly<{ scope: string; options: readonly AreaSnowWatchOption[] }>) {
  const [selected, setSelected] = useState(options[0]?.slug ?? "");
  const selectId = useId();
  if (options.length === 0) return null;
  const at = `[data-snow-scope="${token(scope)}"]`;
  const css = `${at} [data-snow-member]:not([data-snow-member="${token(selected)}"]){display:none}`;
  const current = options.find((option) => option.slug === selected) ?? options[0];
  return (
    <div data-snow-scope={token(scope)}>
      <style>{css}</style>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {options.map((option) => (
          <div key={option.slug} data-snow-member={option.slug} className="min-w-0">
            {option.header}
          </div>
        ))}
        {options.length > 1 && (
          <>
            <label htmlFor={selectId} className="sr-only">Show the snow forecast for</label>
            <span className="relative flex w-full items-center md:w-auto">
              <select
                id={selectId}
                value={selected}
                onChange={(event) => setSelected(event.target.value)}
                className="min-h-11 w-full cursor-pointer appearance-none rounded-[10px] border border-[#848E86] bg-white py-0 pl-3 pr-[34px] text-[14.5px] font-semibold text-navy hover:border-navy md:w-[220px]"
              >
                {options.map((option) => (
                  <option key={option.slug} value={option.slug}>{option.label}</option>
                ))}
              </select>
              <span aria-hidden="true" className="pointer-events-none absolute right-[11px] flex text-teal-dark">
                <AreaChevron />
              </span>
            </span>
          </>
        )}
      </div>
      <p className="sr-only" aria-live="polite">{`Showing the snow forecast for ${current.label}`}</p>
      {options.map((option) => (
        <div key={option.slug} data-snow-member={option.slug}>
          {option.body}
        </div>
      ))}
    </div>
  );
}
